import { useEffect } from "react";
import * as z from "zod";
import { useLocation } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";
import { confirmSignIn, ConfirmSignInInput } from "@aws-amplify/auth";

import { useFormWithSchema } from "hooks/useFormWithSchema";
import AuthLayout from "component/Layout/AuthLayout";
import PasswordTextField from "component/Form/PasswordTextField";
import { useAppNavigate } from "routes/useAppNavigate";
import { passwordSchema } from "schemas/userSchema";
import { notify } from "lib/notify";
import PlaceholderLogo from "./PlaceholderLogo";
import { handleError } from "utils/error/handleError";

const schema = z
  .object({
    password: passwordSchema,
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "パスワードが一致しません",
    path: ["confirmPassword"],
  });

type PasswordSetFormInput = z.infer<typeof schema>;

const PasswordSetPage = () => {
  const { authenticatedNavigate, publicNavigate } = useAppNavigate();
  const location = useLocation();

  const onSubmit = async (data: PasswordSetFormInput) => {
    try {
      const params: ConfirmSignInInput = {
        challengeResponse: data.password,
      };
      await confirmSignIn(params);
      notify("登録しました");
      authenticatedNavigate.root();
    } catch (err) {
      publicNavigate.login();
      handleError(err, {
        overrideMessage: "エラーが発生しています。再度お試しください。",
      });
    }
  };

  const {
    formState: { isSubmitting, errors },
    register,
    onSubmitHandler,
  } = useFormWithSchema(schema, onSubmit);

  useEffect(() => {
    const state = location.state as { signInStep?: string };
    if (
      !state ||
      !state.signInStep ||
      state.signInStep !== "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
    ) {
      publicNavigate.login();
    }
  }, [location.state, publicNavigate]);

  return (
    <AuthLayout>
      <Box
        component={"form"}
        onSubmit={onSubmitHandler}
        noValidate
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
        }}
      >
        <Stack
          spacing={4}
          sx={{
            width: "100%",
          }}
        >
          <Stack spacing={2}>
            <Box display={"flex"} justifyContent={"center"}>
              <PlaceholderLogo />
            </Box>
            <Typography variant="h5">パスワード登録</Typography>
            <Typography variant="body2" color="text.secondary">
              仮パスワードでログインしています。セキュリティ強化のため、パスワードを登録してください。
            </Typography>
          </Stack>
          <Stack spacing={2}>
            <Box>
              <PasswordTextField
                label="新しいパスワード"
                {...register("password")}
                error={!!errors.password}
                helperText={errors.password?.message}
                fullWidth
                disabled={isSubmitting}
              />
              <Typography
                sx={{ mt: 1 }}
                variant="passwordHint"
                color={"text.secondary"}
              >
                {
                  "パスワードは8文字以上20文字以内で、\n大文字・小文字・数字を含む英数字と、記号「-」「_」「.」が使えます。"
                }
              </Typography>
            </Box>
            <PasswordTextField
              label="新しいパスワード（確認用）"
              {...register("confirmPassword")}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword?.message}
              fullWidth
              disabled={isSubmitting}
            />
          </Stack>
          <Stack spacing={2}>
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={isSubmitting}
              startIcon={
                isSubmitting && <CircularProgress size={20} color="inherit" />
              }
            >
              登録
            </Button>
          </Stack>
        </Stack>
      </Box>
    </AuthLayout>
  );
};

export default PasswordSetPage;
